import { memo, useEffect, useRef, type MutableRefObject } from 'react';
import { useWebsiteTheme } from '../../../lib/theme';
import {
  VOICE_WAVEFORM_BAR_ENTER_DELAY_MS,
  VOICE_WAVEFORM_BAR_ENTER_DURATION_MS,
  VOICE_WAVEFORM_BAR_GAP,
  VOICE_WAVEFORM_BAR_WIDTH,
  VOICE_WAVEFORM_BASELINE_HEIGHT,
  VOICE_WAVEFORM_CANVAS_WIDTH_PX,
  VOICE_WAVEFORM_HEIGHT_PX,
  VOICE_WAVEFORM_OVERSCAN_BARS,
  VOICE_WAVEFORM_STEP_MS,
  VOICE_WAVEFORM_ZOOM_OUT_DURATION_MS,
} from './constants';

type VoiceWaveformPhase = 'recording' | 'transcribing';

type VoiceWaveformBar = {
  id: number;
  level: number;
  introducedAt: number | null;
};

type VoiceWaveformPreviewBar = {
  id: number;
  introducedAt: number;
};

type VoiceWaveformTimeline = {
  levels: number[];
  totalDurationBars: number;
};

type VoiceWaveformZoomTransition = {
  startedAt: number;
  sourceDomainStart: number;
};

type VoiceWaveformState = {
  nextBarId: number;
  bars: VoiceWaveformBar[];
  previewBar: VoiceWaveformPreviewBar | null;
  lastTimestamp: number | null;
  elapsedSinceStep: number;
  shiftProgress: number;
  historyLevels: number[];
  fullTimeline: VoiceWaveformTimeline | null;
  zoomTransition: VoiceWaveformZoomTransition | null;
};

type VoiceWaveformViewportMetrics = {
  visibleBarCount: number;
  visibleOffsetPx: number;
};

type VoiceWaveformCanvasResources = {
  context: CanvasRenderingContext2D;
  strokeColor: string;
};

const VOICE_WAVEFORM_BAR_STEP_WIDTH =
  VOICE_WAVEFORM_BAR_WIDTH + VOICE_WAVEFORM_BAR_GAP;
const VOICE_WAVEFORM_VISIBLE_BAR_COUNT = Math.max(
  1,
  Math.floor(
    (VOICE_WAVEFORM_CANVAS_WIDTH_PX + VOICE_WAVEFORM_BAR_GAP) /
      VOICE_WAVEFORM_BAR_STEP_WIDTH
  )
);
const VOICE_WAVEFORM_BUFFER_BAR_COUNT =
  VOICE_WAVEFORM_VISIBLE_BAR_COUNT + VOICE_WAVEFORM_OVERSCAN_BARS;
const VOICE_WAVEFORM_RENDER_WIDTH_PX =
  VOICE_WAVEFORM_VISIBLE_BAR_COUNT * VOICE_WAVEFORM_BAR_WIDTH +
  Math.max(0, VOICE_WAVEFORM_VISIBLE_BAR_COUNT - 1) * VOICE_WAVEFORM_BAR_GAP;
const VOICE_WAVEFORM_OVERSCAN_WIDTH_PX =
  VOICE_WAVEFORM_BAR_STEP_WIDTH * VOICE_WAVEFORM_OVERSCAN_BARS;
const VOICE_WAVEFORM_CENTER_Y = VOICE_WAVEFORM_HEIGHT_PX / 2;
const VOICE_WAVEFORM_ENTER_TOTAL_MS =
  VOICE_WAVEFORM_BAR_ENTER_DELAY_MS + VOICE_WAVEFORM_BAR_ENTER_DURATION_MS;
const VOICE_WAVEFORM_SHARED_EASING = createCubicBezierEasing(0.22, 1, 0.36, 1);

function getViewportMetrics(
  viewportWidth: number | null
): VoiceWaveformViewportMetrics {
  const clampedVisibleWidth = Math.min(
    VOICE_WAVEFORM_RENDER_WIDTH_PX,
    Math.max(
      VOICE_WAVEFORM_BAR_WIDTH,
      viewportWidth ?? VOICE_WAVEFORM_RENDER_WIDTH_PX
    )
  );
  const visibleBarCount = Math.max(
    1,
    Math.floor(
      (clampedVisibleWidth + VOICE_WAVEFORM_BAR_GAP) /
        VOICE_WAVEFORM_BAR_STEP_WIDTH
    )
  );
  const visibleRenderWidthPx =
    visibleBarCount * VOICE_WAVEFORM_BAR_WIDTH +
    Math.max(0, visibleBarCount - 1) * VOICE_WAVEFORM_BAR_GAP;

  return {
    visibleBarCount,
    visibleOffsetPx: VOICE_WAVEFORM_RENDER_WIDTH_PX - visibleRenderWidthPx,
  };
}

function createCubicBezierEasing(
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const a = (start: number, end: number) => 1 - 3 * end + 3 * start;
  const b = (start: number, end: number) => 3 * end - 6 * start;
  const c = (start: number) => 3 * start;

  const sampleCurve = (t: number, start: number, end: number) =>
    ((a(start, end) * t + b(start, end)) * t + c(start)) * t;
  const sampleSlope = (t: number, start: number, end: number) =>
    3 * a(start, end) * t * t + 2 * b(start, end) * t + c(start);

  const solveCurveT = (x: number) => {
    let estimate = x;

    for (let iteration = 0; iteration < 4; iteration += 1) {
      const slope = sampleSlope(estimate, x1, x2);
      if (Math.abs(slope) < 1e-6) {
        break;
      }

      const error = sampleCurve(estimate, x1, x2) - x;
      estimate -= error / slope;
    }

    let lower = 0;
    let upper = 1;
    estimate = Math.min(Math.max(estimate, lower), upper);

    for (let iteration = 0; iteration < 8; iteration += 1) {
      const error = sampleCurve(estimate, x1, x2) - x;
      if (Math.abs(error) <= 1e-6) {
        break;
      }

      if (error > 0) {
        upper = estimate;
      } else {
        lower = estimate;
      }
      estimate = (lower + upper) / 2;
    }

    return estimate;
  };

  return (progress: number) => {
    if (progress <= 0) {
      return 0;
    }
    if (progress >= 1) {
      return 1;
    }
    return sampleCurve(solveCurveT(progress), y1, y2);
  };
}

function shapeWaveformLevel(level: number) {
  const clampedLevel = Math.max(0, Math.min(level, 1));
  return Math.min(Math.pow(clampedLevel, 0.76), 1);
}

function createWaveformBars(state: VoiceWaveformState) {
  return Array.from({ length: VOICE_WAVEFORM_BUFFER_BAR_COUNT }, () => ({
    id: state.nextBarId++,
    level: 0,
    introducedAt: null,
  }));
}

function resetWaveformState(state: VoiceWaveformState, timestamp: number) {
  state.bars = createWaveformBars(state);
  state.previewBar = {
    id: state.nextBarId++,
    introducedAt: timestamp,
  };
  state.lastTimestamp = null;
  state.elapsedSinceStep = 0;
  state.shiftProgress = 0;
  state.historyLevels = [];
  state.fullTimeline = null;
  state.zoomTransition = null;
}

function ensureCanvasResolution(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D
) {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const width = Math.round(VOICE_WAVEFORM_RENDER_WIDTH_PX * devicePixelRatio);
  const height = Math.round(VOICE_WAVEFORM_HEIGHT_PX * devicePixelRatio);

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${VOICE_WAVEFORM_RENDER_WIDTH_PX}px`;
    canvas.style.height = `${VOICE_WAVEFORM_HEIGHT_PX}px`;
  }

  context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
}

function resolveCanvasResources(
  canvas: HTMLCanvasElement,
  contextRef: MutableRefObject<CanvasRenderingContext2D | null>,
  strokeColorRef: MutableRefObject<string>
): VoiceWaveformCanvasResources | null {
  const context = contextRef.current ?? canvas.getContext('2d');
  if (!context) {
    return null;
  }

  contextRef.current = context;
  if (!strokeColorRef.current) {
    strokeColorRef.current = window.getComputedStyle(canvas).color;
  }

  return {
    context,
    strokeColor: strokeColorRef.current,
  };
}

function getWaveformEnterProgress(
  introducedAt: number | null,
  timestamp: number
) {
  if (introducedAt == null) {
    return 1;
  }

  const elapsed = timestamp - introducedAt;
  if (elapsed <= VOICE_WAVEFORM_BAR_ENTER_DELAY_MS) {
    return 0;
  }
  if (elapsed >= VOICE_WAVEFORM_ENTER_TOTAL_MS) {
    return 1;
  }

  return VOICE_WAVEFORM_SHARED_EASING(
    (elapsed - VOICE_WAVEFORM_BAR_ENTER_DELAY_MS) /
      VOICE_WAVEFORM_BAR_ENTER_DURATION_MS
  );
}

function getPreviewLevel(
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>
) {
  return (
    (state.bars[state.bars.length - 1]?.level ?? 0) * 0.3 +
    liveLevelRef.current * 0.7
  );
}

function getZoomOutProgress(
  transition: VoiceWaveformZoomTransition | null,
  timestamp: number
) {
  if (!transition) {
    return 1;
  }

  return Math.min(
    Math.max(
      (timestamp - transition.startedAt) / VOICE_WAVEFORM_ZOOM_OUT_DURATION_MS,
      0
    ),
    1
  );
}

function getWaveformLevelAt(levels: number[], index: number) {
  if (index < 0 || index >= levels.length) {
    return 0;
  }

  return levels[index] ?? 0;
}

function sampleWaveformPeak(levels: number[], start: number, end: number) {
  if (end <= start) {
    return 0;
  }

  let peak = 0;
  const firstIndex = Math.floor(start);
  const lastIndex = Math.ceil(end);

  for (let index = firstIndex; index < lastIndex; index += 1) {
    const overlapStart = Math.max(start, index);
    const overlapEnd = Math.min(end, index + 1);
    if (overlapEnd <= overlapStart) {
      continue;
    }

    peak = Math.max(peak, getWaveformLevelAt(levels, index));
  }

  return peak;
}

function createFullTimeline(
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>
) {
  const committedBars = state.historyLevels.length;
  const previewProgress = state.shiftProgress;
  const totalDurationBars = committedBars + previewProgress;

  if (totalDurationBars <= 0) {
    return null;
  }

  const levels =
    previewProgress > 0 || committedBars === 0
      ? [...state.historyLevels, getPreviewLevel(state, liveLevelRef)]
      : [...state.historyLevels];

  return {
    levels,
    totalDurationBars,
  };
}

function startZoomOutTransition(
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>,
  timestamp: number,
  visibleBarCount: number
) {
  const fullTimeline = createFullTimeline(state, liveLevelRef);
  state.fullTimeline = fullTimeline;
  state.zoomTransition = fullTimeline
    ? {
        startedAt: timestamp,
        sourceDomainStart: fullTimeline.totalDurationBars - visibleBarCount,
      }
    : null;
  state.previewBar = null;
  state.lastTimestamp = null;
}

function updateZoomOutViewport(
  state: VoiceWaveformState,
  visibleBarCount: number
) {
  if (!state.zoomTransition || !state.fullTimeline) {
    return;
  }

  state.zoomTransition = {
    ...state.zoomTransition,
    sourceDomainStart: state.fullTimeline.totalDurationBars - visibleBarCount,
  };
}

function advanceRecordingWaveform(
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>,
  timestamp: number
) {
  const lastTimestamp = state.lastTimestamp ?? timestamp;
  const deltaMs = Math.min(48, timestamp - lastTimestamp);
  state.lastTimestamp = timestamp;
  state.elapsedSinceStep += deltaMs;

  while (state.elapsedSinceStep >= VOICE_WAVEFORM_STEP_MS) {
    state.elapsedSinceStep -= VOICE_WAVEFORM_STEP_MS;

    const previousLevel = state.bars[state.bars.length - 1]?.level ?? 0;
    const injectedLevel = previousLevel * 0.38 + liveLevelRef.current * 0.62;
    const previewBar = state.previewBar ?? {
      id: state.nextBarId++,
      introducedAt: timestamp,
    };

    state.bars.shift();
    state.bars.push({
      id: previewBar.id,
      level: injectedLevel,
      introducedAt: previewBar.introducedAt,
    });
    state.historyLevels.push(injectedLevel);
    state.previewBar = {
      id: state.nextBarId++,
      introducedAt: timestamp,
    };
  }

  state.shiftProgress = state.elapsedSinceStep / VOICE_WAVEFORM_STEP_MS;
}

function drawWaveformBars(
  context: CanvasRenderingContext2D,
  bars: Array<{
    level: number;
    x: number;
    introducedAt?: number | null;
  }>,
  timestamp: number
) {
  if (bars.length === 0) {
    return false;
  }

  context.beginPath();
  context.globalAlpha = 0.2;
  for (const bar of bars) {
    context.moveTo(
      bar.x,
      VOICE_WAVEFORM_CENTER_Y - VOICE_WAVEFORM_BASELINE_HEIGHT / 2
    );
    context.lineTo(
      bar.x,
      VOICE_WAVEFORM_CENTER_Y + VOICE_WAVEFORM_BASELINE_HEIGHT / 2
    );
  }
  context.stroke();

  let hasPendingEnterAnimation = false;

  for (const bar of bars) {
    const shapedLevel = shapeWaveformLevel(bar.level);
    const activeHeight =
      VOICE_WAVEFORM_BASELINE_HEIGHT +
      shapedLevel *
        (VOICE_WAVEFORM_HEIGHT_PX - VOICE_WAVEFORM_BASELINE_HEIGHT) *
        0.96;

    if (activeHeight <= VOICE_WAVEFORM_BASELINE_HEIGHT) {
      continue;
    }

    const enterProgress =
      bar.introducedAt == null
        ? 1
        : getWaveformEnterProgress(bar.introducedAt, timestamp);
    if (enterProgress < 1) {
      hasPendingEnterAnimation = true;
    }
    if (enterProgress <= 0) {
      continue;
    }

    const startScale = Math.min(
      1,
      VOICE_WAVEFORM_BASELINE_HEIGHT / activeHeight
    );
    const currentScale = startScale + (1 - startScale) * enterProgress;
    const currentHalfHeight = (activeHeight * currentScale) / 2;

    context.beginPath();
    context.globalAlpha = enterProgress;
    context.moveTo(bar.x, VOICE_WAVEFORM_CENTER_Y - currentHalfHeight);
    context.lineTo(bar.x, VOICE_WAVEFORM_CENTER_Y + currentHalfHeight);
    context.stroke();
  }

  context.globalAlpha = 1;
  return hasPendingEnterAnimation;
}

function paintRecordingWaveform(
  context: CanvasRenderingContext2D,
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>,
  timestamp: number
) {
  const previewLevel = state.previewBar
    ? getPreviewLevel(state, liveLevelRef)
    : null;
  const renderedBars =
    state.previewBar && previewLevel != null
      ? [
          ...state.bars,
          {
            id: state.previewBar.id,
            level: previewLevel,
            introducedAt: state.previewBar.introducedAt,
          },
        ]
      : state.bars;
  const visibleBars = renderedBars
    .map((bar, index) => ({
      ...bar,
      x:
        index * VOICE_WAVEFORM_BAR_STEP_WIDTH +
        VOICE_WAVEFORM_BAR_WIDTH / 2 -
        state.shiftProgress * VOICE_WAVEFORM_BAR_STEP_WIDTH,
    }))
    .filter(
      bar =>
        bar.x >= -VOICE_WAVEFORM_OVERSCAN_WIDTH_PX &&
        bar.x <=
          VOICE_WAVEFORM_RENDER_WIDTH_PX + VOICE_WAVEFORM_OVERSCAN_WIDTH_PX
    );

  drawWaveformBars(context, visibleBars, timestamp);
  return true;
}

function paintZoomedWaveform(
  context: CanvasRenderingContext2D,
  state: VoiceWaveformState,
  viewportMetrics: VoiceWaveformViewportMetrics,
  timestamp: number
) {
  const fullTimeline = state.fullTimeline;
  if (!fullTimeline) {
    return false;
  }

  const zoomProgress = getZoomOutProgress(state.zoomTransition, timestamp);
  const easedProgress = VOICE_WAVEFORM_SHARED_EASING(zoomProgress);
  const domainStart =
    (state.zoomTransition?.sourceDomainStart ?? 0) * (1 - easedProgress);
  const domainEnd = fullTimeline.totalDurationBars;
  const domainWidth = domainEnd - domainStart;

  if (domainWidth <= 0) {
    return false;
  }

  const visibleBars = Array.from(
    { length: viewportMetrics.visibleBarCount },
    (_, index) => {
      const slotStart =
        domainStart + (domainWidth * index) / viewportMetrics.visibleBarCount;
      const slotEnd =
        domainStart +
        (domainWidth * (index + 1)) / viewportMetrics.visibleBarCount;

      return {
        level: sampleWaveformPeak(fullTimeline.levels, slotStart, slotEnd),
        x:
          viewportMetrics.visibleOffsetPx +
          index * VOICE_WAVEFORM_BAR_STEP_WIDTH +
          VOICE_WAVEFORM_BAR_WIDTH / 2,
      };
    }
  );

  drawWaveformBars(context, visibleBars, timestamp);

  if (zoomProgress >= 1) {
    state.zoomTransition = null;
    return false;
  }

  return true;
}

function paintWaveform(
  canvas: HTMLCanvasElement | null,
  resources: VoiceWaveformCanvasResources | null,
  phase: VoiceWaveformPhase,
  state: VoiceWaveformState,
  liveLevelRef: MutableRefObject<number>,
  viewportMetrics: VoiceWaveformViewportMetrics,
  timestamp: number
) {
  if (!canvas || !resources) {
    return phase === 'recording';
  }

  const { context, strokeColor } = resources;

  ensureCanvasResolution(canvas, context);

  context.clearRect(
    0,
    0,
    VOICE_WAVEFORM_RENDER_WIDTH_PX,
    VOICE_WAVEFORM_HEIGHT_PX
  );
  context.lineWidth = VOICE_WAVEFORM_BAR_WIDTH;
  context.lineCap = 'round';
  context.strokeStyle = strokeColor;

  if (phase === 'recording') {
    return paintRecordingWaveform(context, state, liveLevelRef, timestamp);
  }

  return paintZoomedWaveform(context, state, viewportMetrics, timestamp);
}

export const ChatComposerVoiceWaveformCanvas = memo(
  function ChatComposerVoiceWaveformCanvas({
    phase,
    liveLevelRef,
    viewportWidth,
  }: {
    phase: VoiceWaveformPhase;
    liveLevelRef: MutableRefObject<number>;
    viewportWidth: number | null;
  }) {
    const { resolvedTheme } = useWebsiteTheme();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const strokeColorRef = useRef('');
    const stateRef = useRef<VoiceWaveformState>({
      nextBarId: 0,
      bars: [],
      previewBar: null,
      lastTimestamp: null,
      elapsedSinceStep: 0,
      shiftProgress: 0,
      historyLevels: [],
      fullTimeline: null,
      zoomTransition: null,
    });
    const previousPhaseRef = useRef<VoiceWaveformPhase | null>(null);

    useEffect(() => {
      const state = stateRef.current;
      const viewportMetrics = getViewportMetrics(viewportWidth);
      contextRef.current = canvasRef.current?.getContext('2d') ?? null;
      strokeColorRef.current = '';

      if (phase === 'recording') {
        if (previousPhaseRef.current !== 'recording') {
          resetWaveformState(state, performance.now());
        } else {
          state.lastTimestamp = null;
        }
      } else if (previousPhaseRef.current === 'recording') {
        startZoomOutTransition(
          state,
          liveLevelRef,
          performance.now(),
          viewportMetrics.visibleBarCount
        );
      } else if (state.zoomTransition) {
        updateZoomOutViewport(state, viewportMetrics.visibleBarCount);
        state.lastTimestamp = null;
      } else {
        state.lastTimestamp = null;
      }
      previousPhaseRef.current = phase;

      let frameId: number | null = null;
      let isCancelled = false;

      const tick = (timestamp: number) => {
        if (isCancelled) {
          return;
        }

        if (phase === 'recording') {
          advanceRecordingWaveform(state, liveLevelRef, timestamp);
        }

        if (
          paintWaveform(
            canvasRef.current,
            canvasRef.current
              ? resolveCanvasResources(
                  canvasRef.current,
                  contextRef,
                  strokeColorRef
                )
              : null,
            phase,
            state,
            liveLevelRef,
            viewportMetrics,
            timestamp
          )
        ) {
          frameId = window.requestAnimationFrame(tick);
        } else {
          frameId = null;
        }
      };

      frameId = window.requestAnimationFrame(tick);

      return () => {
        isCancelled = true;
        if (frameId != null) {
          window.cancelAnimationFrame(frameId);
        }
      };
    }, [liveLevelRef, phase, resolvedTheme, viewportWidth]);

    return (
      <div className="relative h-8 w-full overflow-hidden">
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute top-0 right-0 block h-8 shrink-0"
          style={{ width: `${VOICE_WAVEFORM_RENDER_WIDTH_PX}px` }}
        />
      </div>
    );
  }
);
